import React from 'react';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { BarChart } from '../charts/BarChart';
import { Skeleton } from '../ui/Skeleton';
import { useAnalytics } from '../../hooks/useAnalytics';
import { useNavigate } from 'react-router-dom';
import { ResponsiveContainer, AreaChart, Area, CartesianGrid, XAxis, YAxis, Tooltip, PieChart, Pie, Cell, Legend } from 'recharts';
import { 
  Users, 
  TrendingUp, 
  DollarSign, 
  MousePointer,
  ExternalLink,
  Calendar,
  Globe,
  Loader,
  BarChart3,
  Activity,
  Clock,
  Zap,
  AlertTriangle
} from 'lucide-react';

interface ProjectOverviewProps {
  project: any;
}

export function ProjectOverview({ project }: ProjectOverviewProps) {
  const navigate = useNavigate();
  const analytics = useAnalytics(project.id);
  
  // Get analytics overview for the last 30 days
  const today = new Date();
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(today.getDate() - 30);
  
  const { data: analyticsData, isLoading, error } = analytics.useOverview({
    from: thirtyDaysAgo,
    to: today
  });
  
  // Use analytics data if available, otherwise fallback to project stats
  const stats = analyticsData?.overview || project.stats || {
    sessions: 0,
    users: 0,
    conversions: 0,
    revenue: 0,
    bounceRate: 0,
    avgSessionDuration: 0
  };
  
  // Calculate bounce rate as percentage with 1 decimal place
  const bounceRatePercent = stats.bounceRate ? (stats.bounceRate * 100).toFixed(1) : '0.0';
  
  // Helper function to parse GA4 date format (YYYYMMDD) to readable format
  const parseGA4Date = (dateString: string) => {
    if (!dateString) return 'Invalid Date';
    
    // Handle different date formats
    if (dateString.includes('-')) {
      // Already in YYYY-MM-DD format
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'Invalid Date';
      }
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } else if (dateString.length === 8) {
      // GA4 format: YYYYMMDD
      const year = dateString.substring(0, 4);
      const month = dateString.substring(4, 6);
      const day = dateString.substring(6, 8);
      const date = new Date(`${year}-${month}-${day}`);
      if (isNaN(date.getTime())) {
        return 'Invalid Date';
      }
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } else {
      // Try to parse as regular date
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'Invalid Date';
      }
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  };

  // Generate chart data from analytics response
  const trafficData = analyticsData?.overview?.dailyTrend?.map((day: any) => ({
    date: parseGA4Date(day.date),
    sessions: day.sessions || 0,
    users: day.users || 0
  })) || Array.from({ length: 30 }, (_, i) => ({
    date: new Date(Date.now() - (29 - i) * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    sessions: Math.floor(Math.random() * 500) + 200,
    users: Math.floor(Math.random() * 350) + 150
  }));

  // Process traffic sources - take top 7 and use actual GA4 names
  const sourceData = analyticsData?.overview?.trafficSources?.slice(0, 7)?.map((source: any, index: number) => ({
    name: source.source, // Use actual GA4 channel names
    value: source.percentage,
    color: getColorForSource(source.source, index)
  })) || [
    { name: 'Organic Search', value: 45, color: '#3b82f6' },
    { name: 'Direct', value: 25, color: '#8b5cf6' },
    { name: 'Social Media', value: 18, color: '#10b981' },
    { name: 'Referral', value: 12, color: '#f59e0b' }
  ];

  const deviceData = analyticsData?.overview?.deviceBreakdown || [
    { device: 'Desktop', sessions: 3200, users: 2100 },
    { device: 'Mobile', sessions: 2800, users: 1900 },
    { device: 'Tablet', sessions: 800, users: 600 }
  ];
  
  function getColorForSource(source: string, index: number = 0) {
    // Define a comprehensive color palette for GA4 channel names
    const ga4Colors: Record<string, string> = {
      // Primary channels
      'Organic Search': '#3b82f6',      // Blue
      'Direct': '#8b5cf6',              // Purple  
      'Paid Search': '#f59e0b',         // Orange
      'Display': '#10b981',             // Green
      'Social': '#ec4899',              // Pink
      'Email': '#06b6d4',               // Cyan
      'Referral': '#84cc16',            // Lime
      'Affiliates': '#f97316',          // Orange-red
      'Video': '#ef4444',               // Red
      'Audio': '#a855f7',               // Purple-light
      'SMS': '#14b8a6',                 // Teal
      'Mobile Push Notifications': '#6366f1', // Indigo
      'Unassigned': '#6b7280',          // Gray
      'Organic Social': '#ec4899',      // Pink
      'Paid Social': '#db2777',         // Pink-dark
      'Organic Video': '#ef4444',       // Red
      'Paid Video': '#dc2626',          // Red-dark
    };
    
    // First try exact match with GA4 channel name
    if (ga4Colors[source]) {
      return ga4Colors[source];
    }
    
    // Then try partial matches for common variations
    const lowerSource = source.toLowerCase();
    if (lowerSource.includes('organic') && lowerSource.includes('search')) return '#3b82f6';
    if (lowerSource.includes('direct')) return '#8b5cf6';
    if (lowerSource.includes('paid') && lowerSource.includes('search')) return '#f59e0b';
    if (lowerSource.includes('display')) return '#10b981';
    if (lowerSource.includes('social')) return '#ec4899';
    if (lowerSource.includes('email')) return '#06b6d4';
    if (lowerSource.includes('referral')) return '#84cc16';
    if (lowerSource.includes('video')) return '#ef4444';
    if (lowerSource.includes('unassigned')) return '#6b7280';
    
    // Fallback to a color palette based on index
    const fallbackColors = [
      '#3b82f6', '#8b5cf6', '#f59e0b', '#10b981', '#ec4899', '#06b6d4', '#84cc16'
    ];
    return fallbackColors[index % fallbackColors.length] || '#6b7280';
  }

  const handleTabChange = (tab: string) => {
    navigate(`/projects/${project.id}?tab=${tab}`);
  };

  if (error) {
    return (
      <div className="space-y-6">
        <Card className="p-6 bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-700">
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 mr-3" />
            <div>
              <h3 className="font-semibold text-red-900 dark:text-red-300">Error Loading Analytics Data</h3>
              <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                {error?.message || 'Failed to fetch analytics data. Please try refreshing the page.'}
              </p>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        {/* Project Info Skeleton */}
        <Card className="p-6">
          <div className="flex items-start justify-between">
            <div className="flex-1 space-y-4">
              <div className="flex items-center space-x-3">
                <Skeleton className="h-6 w-48" />
                <Skeleton className="h-6 w-20" />
                <Skeleton className="h-6 w-24" />
              </div>
              <Skeleton className="h-4 w-full" />
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-4 w-28" />
              </div>
            </div>
          </div>
        </Card>

        {/* Key Metrics Skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className="p-6">
              <div className="flex items-center justify-between">
                <div className="space-y-2">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-8 w-20" />
                  <Skeleton className="h-4 w-32" />
                </div>
                <Skeleton className="h-12 w-12 rounded-lg" />
              </div>
            </Card>
          ))}
        </div>

        {/* Charts Skeleton */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="p-6">
            <Skeleton className="h-6 w-48 mb-4" />
            <Skeleton className="h-64 w-full" />
          </Card>
          <Card className="p-6">
            <Skeleton className="h-6 w-48 mb-4" />
            <Skeleton className="h-64 w-full" />
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* GA4 Connection Status */}
      {analyticsData?.dataSource === 'ga4_real' && (
        <Card className="p-4 bg-gradient-to-r from-green-50 dark:from-green-900/20 to-blue-50 dark:to-blue-900/20 border-green-200 dark:border-green-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="w-8 h-8 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center mr-3">
                <Activity className="w-4 h-4 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white">Real GA4 Data Connected</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Showing live data from your Google Analytics 4 property (ID: {project.ga4PropertyId})
                </p>
              </div>
            </div>
            <div className="text-xs text-green-600 dark:text-green-400 font-medium">
              Last updated: {analyticsData?.lastUpdated ? new Date(analyticsData.lastUpdated).toLocaleTimeString() : 'Now'}
            </div>
          </div>
        </Card>
      )}

      {/* Connection Status Banner - Only show if GA4 is NOT actually connected and working */}
      {analyticsData?.dataSource !== 'ga4_real' && !project.ga4PropertyId && (
        <Card className="p-4 bg-gradient-to-r from-yellow-50 dark:from-yellow-900/20 to-orange-50 dark:to-orange-900/20 border-yellow-200 dark:border-yellow-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="w-8 h-8 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg flex items-center justify-center mr-3">
                <Zap className="w-4 h-4 text-yellow-600 dark:text-yellow-400" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white">Connect Google Analytics</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">Connect your GA4 property to see real analytics data</p>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={() => handleTabChange('connections')}>
              Connect Now
            </Button>
          </div>
        </Card>
      )}

      {/* Mock Data Warning - Only show if GA4 property is configured but not returning real data */}
      {project.ga4PropertyId && analyticsData?.dataSource !== 'ga4_real' && (
        <Card className="p-4 bg-gradient-to-r from-orange-50 dark:from-orange-900/20 to-red-50 dark:to-red-900/20 border-orange-200 dark:border-orange-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="w-8 h-8 bg-orange-100 dark:bg-orange-900/30 rounded-lg flex items-center justify-center mr-3">
                <AlertTriangle className="w-4 h-4 text-orange-600 dark:text-orange-400" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white">Using Sample Data</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  GA4 is connected but showing sample data. {analyticsData?.dataSource === 'ga4_mock' ? 'Real data fetch failed.' : 'No analytics data available.'}
                </p>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={() => window.location.reload()}>
              Retry
            </Button>
          </div>
        </Card>
      )}

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Sessions</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.sessions?.toLocaleString() || '0'}</p>
              <p className="text-sm text-green-600 dark:text-green-400 mt-1">
                {analyticsData ? 'Live data' : 'Sample data'}
              </p>
            </div>
            <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Unique Users</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.users?.toLocaleString() || '0'}</p>
              <p className="text-sm text-green-600 dark:text-green-400 mt-1">
                {analyticsData ? 'Live data' : 'Sample data'}
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
              <Users className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Conversions</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.conversions || '0'}</p>
              <p className="text-sm text-green-600 dark:text-green-400 mt-1">
                {analyticsData ? 'Live data' : 'Sample data'}
              </p>
            </div>
            <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
              <MousePointer className="w-6 h-6 text-purple-600 dark:text-purple-400" />
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Bounce Rate</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{bounceRatePercent}%</p>
              <p className="text-sm text-green-600 dark:text-green-400 mt-1">
                {analyticsData ? 'Live data' : 'Sample data'}
              </p>
            </div>
            <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900/30 rounded-lg flex items-center justify-center">
              <Clock className="w-6 h-6 text-orange-600 dark:text-orange-400" />
            </div>
          </div>
        </Card>
      </div>

      {/* Error State */}
      {error && (
        <Card className="p-6 border-red-200 bg-red-50">
          <div className="flex items-center">
            <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center mr-3">
              <TrendingUp className="w-4 h-4 text-red-600" />
            </div>
            <div>
              <h3 className="font-semibold text-red-900">Analytics Error</h3>
              <p className="text-sm text-red-600">Unable to load analytics data. Using sample data instead.</p>
            </div>
          </div>
        </Card>
      )}

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Traffic Trend */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Traffic Trend (Last 30 Days)</h3>
            {!analyticsData && (
              <Badge variant="secondary" className="text-xs">Sample Data</Badge>
            )}
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart
              data={trafficData}
              margin={{
                top: 5,
                right: 30,
                left: 20,
                bottom: 5,
              }}
            >
              <defs>
                <linearGradient id="sessionsGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="usersGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10B981" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis 
                dataKey="date" 
                stroke="#6B7280"
                fontSize={12}
                tickFormatter={(value) => {
                  // If the value is already formatted (e.g., "Jan 15"), return it as is
                  if (typeof value === 'string' && value.includes(' ')) {
                    return value;
                  }
                  // Otherwise, try to parse it as a date
                  const date = new Date(value);
                  if (isNaN(date.getTime())) {
                    return value; // Return the original value if it can't be parsed
                  }
                  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                }}
              />
              <YAxis stroke="#6B7280" fontSize={12} />
              <Tooltip 
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '0.5rem',
                  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
                }}
                labelFormatter={(value) => {
                  // If the value is already formatted (e.g., "Jan 15"), try to parse it back to a full date
                  if (typeof value === 'string' && value.includes(' ')) {
                    // Try to parse formatted date like "Jan 15"
                    const currentYear = new Date().getFullYear();
                    const date = new Date(`${value} ${currentYear}`);
                    if (!isNaN(date.getTime())) {
                      return date.toLocaleDateString('en-US', { 
                        weekday: 'long',
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      });
                    }
                    return value; // Return as is if parsing fails
                  }
                  // Otherwise, try to parse it as a date
                  const date = new Date(value);
                  if (isNaN(date.getTime())) {
                    return value; // Return the original value if it can't be parsed
                  }
                  return date.toLocaleDateString('en-US', { 
                    weekday: 'long',
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  });
                }}
                formatter={(value: any, name: string) => [
                  typeof value === 'number' ? value.toLocaleString() : value,
                  name === 'sessions' ? 'Sessions' :
                  name === 'users' ? 'Users' : name
                ]}
              />
              <Area
                type="monotone"
                dataKey="sessions"
                stroke="#3B82F6"
                strokeWidth={2}
                fill="url(#sessionsGradient)"
                name="sessions"
              />
              <Area
                type="monotone"
                dataKey="users"
                stroke="#10B981"
                strokeWidth={2}
                fill="url(#usersGradient)"
                name="users"
              />
            </AreaChart>
          </ResponsiveContainer>
        </Card>

        {/* Traffic Sources */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Traffic Sources</h3>
            {!analyticsData && (
              <Badge variant="secondary" className="text-xs">Sample Data</Badge>
            )}
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <defs>
                {sourceData.map((entry: any, index: number) => (
                  <radialGradient
                    key={`gradient-${index}`}
                    id={`gradient-${index}`}
                    cx="0.5"
                    cy="0.5"
                    r="0.8"
                  >
                    <stop offset="0%" stopColor={entry.color} stopOpacity={0.9} />
                    <stop offset="50%" stopColor={entry.color} stopOpacity={0.6} />
                    <stop offset="100%" stopColor={entry.color} stopOpacity={0.4} />
                  </radialGradient>
                ))}
              </defs>
              <Pie
                data={sourceData}
                cx="50%"
                cy="50%"
                labelLine={false}
                outerRadius={100}
                innerRadius={60}
                fill="#8884d8"
                dataKey="value"
                stroke="rgba(255, 255, 255, 0.1)"
                strokeWidth={2}
              >
                {sourceData.map((entry: any, index: number) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={`url(#gradient-${index})`}
                  />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '0.5rem',
                  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
                }}
                formatter={(value: any, name: string) => [
                  `${value}%`,
                  name
                ]}
                labelFormatter={(label: string) => `${label}`}
              />
            </PieChart>
          </ResponsiveContainer>
          
          {/* Custom Legend */}
          <div className="mt-4 grid grid-cols-2 gap-2">
            {sourceData.map((entry: any, index: number) => (
              <div key={index} className="flex items-center">
                <div 
                  className="w-3 h-3 rounded-full mr-2 shadow-sm" 
                  style={{ 
                    background: `radial-gradient(circle, ${entry.color}dd 0%, ${entry.color}88 70%, ${entry.color}66 100%)`
                  }}
                ></div>
                <span className="text-sm text-gray-600 dark:text-gray-300 truncate">
                  {entry.name} ({entry.value}%)
                </span>
              </div>
            ))}
          </div>
        </Card>

        {/* Device Breakdown */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Device Breakdown</h3>
            {!analyticsData && (
              <Badge variant="secondary" className="text-xs">Sample Data</Badge>
            )}
          </div>
          <BarChart
            data={deviceData}
            xKey="device"
            yKeys={['sessions', 'users']}
            height={300}
          />
        </Card>

        {/* Quick Actions */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Quick Actions</h3>
          <div className="space-y-3">
            <Button className="w-full justify-start" variant="outline" onClick={() => handleTabChange('analytics')}>
              <TrendingUp className="w-4 h-4 mr-2" />
              View Detailed Analytics
            </Button>
            <Button className="w-full justify-start" variant="outline" onClick={() => handleTabChange('connections')}>
              <Activity className="w-4 h-4 mr-2" />
              Manage Connections
            </Button>
            <Button className="w-full justify-start" variant="outline" onClick={() => handleTabChange('settings')}>
              <MousePointer className="w-4 h-4 mr-2" />
              Edit Project Settings
            </Button>
            <Button 
              className="w-full justify-start bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
              onClick={() => navigate(`/agents/data?projectId=${project.id}`)}
            >
              <TrendingUp className="w-4 h-4 mr-2" />
              Ask AI Agent
            </Button>
          </div>
        </Card>
      </div>

      {/* Analytics Insights */}
      {analyticsData && (
        <Card className="p-6 bg-gradient-to-br from-blue-50 dark:from-blue-900/20 to-purple-50 dark:to-purple-900/20 border-blue-200 dark:border-blue-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center mr-3">
                <BarChart3 className="w-4 h-4 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white">Live Analytics Connected</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Real-time data from your GA4 property is now available across all reports.
                </p>
              </div>
            </div>
            <Button variant="outline" onClick={() => handleTabChange('analytics')}>
              View Full Report
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}